import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useEffect, useState } from "react";
import {
  isMobileDevice,
  hasWalletAvailable,
  getWalletDeepLink,
  saveStateBeforeRedirect,
} from "@/lib/mobileWalletFix";
import { Wallet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface MobileWalletConnectProps {
  onClose?: () => void;
  className?: string;
  /**
   * Default: false.
   * Most dialogs already show a close button; enabling this can cause duplicates.
   */
  showInlineClose?: boolean;
}

export function MobileWalletConnect({ onClose, className, showInlineClose }: MobileWalletConnectProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [hasWallet, setHasWallet] = useState(false);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);

  useEffect(() => {
    setIsMobile(isMobileDevice());
    setHasWallet(hasWalletAvailable());
  }, []);

  const handleDeepLinkConnect = (walletName: string) => {
    saveStateBeforeRedirect({
      action: "connect",
      wallet: walletName,
      page: window.location.pathname,
    });

    const deepLink = getWalletDeepLink(walletName);
    console.log(`[MobileWallet] Opening ${walletName} with deeplink:`, deepLink);

    // Try native scheme first; if it doesn't navigate, fallback to universal link.
    if (walletName === "solflare" && deepLink.startsWith("solflare://")) {
      const fallback = deepLink.replace("solflare://", "https://solflare.com/");
      window.location.href = deepLink;

      window.setTimeout(() => {
        // If we are still here, fallback.
        if (document.visibilityState === "visible") {
          window.location.href = fallback;
        }
      }, 1200);

      return;
    }

    window.location.href = deepLink;
  };

  const handleWalletConnect = (walletName: string) => {
    setIsConnecting(walletName);
    handleDeepLinkConnect(walletName);

    // If user stays on this page, re-enable buttons.
    window.setTimeout(() => setIsConnecting(null), 2000);
  };

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div
      className={`bg-card border border-border rounded-2xl p-6 shadow-2xl max-w-sm w-full mx-4 ${className ?? ""}`}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Connect Wallet</h3>
            <p className="text-xs text-muted-foreground">Choose your preferred wallet</p>
          </div>
        </div>
        {showInlineClose && onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close"
          >
            {/* Intentionally no icon here to avoid duplicate close buttons in dialogs */}
            <span className="sr-only">Close</span>
          </button>
        )}
      </div>
      {children}
    </div>
  );

  // Desktop: always show the standard selector (wrapped in branded UI)
  if (!isMobile) {
    return (
      <Shell>
        <div className="rounded-xl border border-border/50 bg-background/40 p-3">
          <WalletMultiButton />
        </div>
      </Shell>
    );
  }

  // Mobile: if wallet is injected (in-app browser), show standard selector in branded UI
  if (hasWallet) {
    return (
      <Shell>
        <div className="space-y-3">
          <div className="rounded-xl border border-border/50 bg-background/40 p-3">
            <WalletMultiButton />
          </div>
          <p className="text-xs text-muted-foreground">
            You're inside a wallet browser. If connect doesn't open, try Safari/Chrome.
          </p>
        </div>
      </Shell>
    );
  }

  // Mobile without injected wallet: show deep link options
  return (
    <Shell>
      <div className="space-y-3">
        {/* Phantom - Recommended for smoothest experience */}
        <Button
          onClick={() => handleWalletConnect("phantom")}
          disabled={isConnecting !== null}
          variant="outline"
          className="w-full h-14 justify-start gap-4 border-primary/50 bg-primary/5 hover:border-primary hover:bg-primary/10 transition-all"
        >
          {isConnecting === 'phantom' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-primary" />
            </div>
          )}
          <div className="text-left flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium text-foreground">Phantom</p>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary font-medium">Recommended</span>
            </div>
            <p className="text-xs text-muted-foreground">Fastest connection</p>
          </div>
        </Button>

        {/* Solflare - Works but requires extra steps */}
        <Button
          onClick={() => handleWalletConnect("solflare")}
          disabled={isConnecting !== null}
          variant="outline"
          className="w-full h-auto min-h-14 py-3 justify-start gap-4 border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-all"
        >
          {isConnecting === 'solflare' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              <Wallet className="w-4 h-4 text-primary" />
            </div>
          )}
          <div className="text-left">
            <p className="font-medium text-foreground">Solflare</p>
            <p className="text-xs text-muted-foreground">Opens in-app browser → tap Connect</p>
          </div>
        </Button>

        <Button
          onClick={() => handleWalletConnect("backpack")}
          disabled={isConnecting !== null}
          variant="outline"
          className="w-full h-14 justify-start gap-4 border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-all"
        >
          {isConnecting === 'backpack' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">B</span>
            </div>
          )}
          <div className="text-left">
            <p className="font-medium text-foreground">Backpack</p>
            <p className="text-xs text-muted-foreground">xNFT enabled wallet</p>
          </div>
        </Button>
      </div>

      <div className="mt-6 pt-4 border-t border-border/50">
        <p className="text-xs text-muted-foreground text-center">
          Don't have a wallet?{" "}
          <a
            href="https://phantom.app/download"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-medium"
          >
            Download Phantom
          </a>
        </p>
      </div>
    </Shell>
  );
}
